import { RouteComponentProps } from "@reach/router";
import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";

import LayoutContext from "../context/layout-context";
import Button from "../components/button/Button";
import { AppServiceCreate, AppServiceCreations } from "../utils/helpers/types";
import { initialPaginationData } from "../utils/constants";
import styles from "./self-service.module.scss";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import Table, { Column } from "../components/table/Table";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import Modal, { ModalProps } from "../components/modal/Modal";
import { resources } from "../utils/resources";
import Input from "../components/input/Input";
import style from "./history.module.scss";
import {
  createAppService,
  getServices,
  updateAppService
} from "../utils/helpers/data/app-service";
import { axiosInstanceBatchCredit } from "../utils/helpers/axios";

const AppService: FunctionComponent<RouteComponentProps> = () => {
  let timerRef: NodeJS.Timeout;
  const { notify } = useContext(LayoutContext);

  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const [appServiceOnEdit, setappServiceOnEdit] =
    useState<AppServiceCreations | null>(null);

  const [appServiceOnView, setappServiceOnView] =
    useState<AppServiceCreations | null>(null);

  const [reset, setReset] = useState(false);

  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [appId, setAppId] = useState<string>("");
  const [consumerQueue, setConsumerQueue] = useState<string>("");

  const [AppServiceCreate, setAppServiceCreate] = useState<
    AppServiceCreations[]
  >([]);
  const [services, setServices] = useState<AppServiceCreations[]>([]);
  const [loading, setLoading] = useState(false);

  const showResetFilter = appId || consumerQueue;

  const resetFilters = () => {
    setReset(true);
    setAppId("");
    setConsumerQueue("");
    setPagination({ ...pagination, pageNumber: 1 });
  };

  const displayAddEditModal = (
    AppServiceCreate: AppServiceCreations | null
  ) => {
    setappServiceOnEdit(AppServiceCreate);
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setappServiceOnEdit(null);
    setShowAddEditModal(false);
  };

  const fetchAppService = async (
    pageNumber = pagination.pageNumber,
    pageSize = pagination.pageSize
  ) => {
    setLoading(true);
    const { data, error, message } = await getServices(
      pageNumber,
      pageSize,
      appId,
      consumerQueue
    );
    setLoading(false);

    if (error || !data) {
      notify("error", `Unable to fetch App service: ${message}`);
      return;
    }

    setServices(data.records);
    setPagination({
      ...pagination,
      pageNumber,
      total: data.total
    });

    setReset(false);
  };

  const handleViewReport = async () => {
    setLoading(true);
    try {
      setPagination({ ...pagination, pageNumber: 1 });
      await fetchAppService(pagination.pageNumber, pagination.pageSize);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      notify("error", "An error occurred while fetching map acquirer.", error);
    }
  };

  useEffect(() => {
    if (!reset) {
      fetchAppService();
    } else {
      // Reset logic
      setPagination({ ...initialPaginationData });
      setReset(false);
    }
  }, [reset]);

  const columns: Column<AppServiceCreations>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "App Code",
      dataIndex: "appCode",
      key: "appCode"
    },
    {
      title: "Bootstrap Server",
      dataIndex: "bootstrapServer",
      key: "bootstrapServer"
    },
    {
      title: "Group Id",
      dataIndex: "groupId",
      key: "groupId"
    },
    {
      title: "Consumer Queue",
      dataIndex: "consumerQueue",
      key: "consumerQueue"
    },

    {
      title: "",
      render: (_: unknown, appService: AppServiceCreations) => {
        const options: ContextOption[] = [
          {
            label: "View Details",
            onClick: () => {
              setappServiceOnView(appService);
            }
          },
          {
            label: "Edit",
            onClick: () => {
              displayAddEditModal(appService);
            }
          }
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      }
    }
  ];

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">Application Service</h1>

      <div className="vertical-margin spaced">
        <div className={style.cont}>
          <div className={style.container}>
            <div className={style.cont1}>
              <div className="input-group half-width">
                <Input
                  value={appId}
                  onChange={(value: string) => setAppId(value)}
                  placeholder="Enter appCode"
                />
              </div>

              <div className="input-group half-width">
                <Input
                  value={consumerQueue}
                  onChange={(value: string) => setConsumerQueue(value)}
                  placeholder="Enter consumer queue"
                />
              </div>
            </div>

            <div className={style.inputView}>
              <Button
                type="accent"
                size="large"
                className="strong no-pdf-print"
                onClick={handleViewReport}
              >
                Filter record
              </Button>

              <span className={style.button} />
            </div>
          </div>
        </div>
        {showResetFilter && (
          <Button type="accent" size="small" onClick={resetFilters}>
            RESET FILTERS
          </Button>
        )}
      </div>

      <br />
      <br />

      <div className="vertical-margin flex end">
        <Button onClick={() => displayAddEditModal(null)}>
          Create App Service
        </Button>
      </div>

      <Table
        dataSource={services || []}
        columns={columns}
        pagination={pagination}
        onPageChange={fetchAppService}
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
        loading={loading}
        className="smaller"
      />

      <AppServiceViewModal
        appService={appServiceOnView}
        cancel={() => setappServiceOnView(null)}
        refresh={fetchAppService}
        visible={Boolean(appServiceOnView)}
      />

      <AddEditModal
        visible={showAddEditModal}
        appServiceOnEdit={appServiceOnEdit}
        cancel={dismissAddEditModal}
        refresh={stayOnPage =>
          fetchAppService(stayOnPage ? pagination.pageNumber : 1)
        }
        appServiceCreation={AppServiceCreate}
      />
    </main>
  );
};

const blankappService: AppServiceCreate = {
  name: "",
  appCode: "",
  bootstrapServer: "",
  groupId: "",
  consumerQueue: ""
};

const AddEditModal: FunctionComponent<
  ModalProps & {
    appServiceOnEdit: AppServiceCreations | null;
    refresh: (stayOnPage: boolean) => void;
    appServiceCreation: AppServiceCreations[];
    cancel: () => void;
  }
> = ({ visible, cancel, appServiceOnEdit, refresh, appServiceCreation }) => {
  const [formData, setFormData] = useState<AppServiceCreate>({
    ...blankappService,
    ...appServiceOnEdit
  });

  const [isSaving, setIsSaving] = useState(false);
  const { notify } = useContext(LayoutContext);

  const handleChange = (key: keyof AppServiceCreate, value: unknown) => {
    setFormData(prevFormData => ({ ...prevFormData, [key]: value }));
  };

  useEffect(() => {
    setFormData(
      appServiceOnEdit
        ? { ...blankappService, ...appServiceOnEdit }
        : { ...blankappService }
    );
  }, [visible, appServiceOnEdit]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (formData.name.length < 4) {
      notify("error", "Name should have a minimum length of 4 characters");
      return;
    }

    setIsSaving(true);

    let id: number | undefined;

    if (appServiceOnEdit && typeof appServiceOnEdit.id === "number") {
      id = appServiceOnEdit.id;
    }

    if (id !== undefined) {
      const { error, message, data } = await updateAppService(id, {
        ...formData
      });

      if (error) {
        notify("error", `Unable to update app service: ${message}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully updated App service");
    } else {
      const { error, message, data } = await createAppService({
        ...formData
      });

      if (error) {
        notify("error", `Unable to create app service: ${message}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully created App service");
    }
    cancel?.();
    refresh(false);
    setIsSaving(false);
  };

  useEffect(() => {
    if (!isCreateOperation && appServiceOnEdit) {
      setFormData({
        name: appServiceOnEdit.name,
        appCode: appServiceOnEdit.appCode,
        bootstrapServer: appServiceOnEdit.bootstrapServer,
        groupId: appServiceOnEdit.groupId,
        consumerQueue: appServiceOnEdit.consumerQueue
      });
    }
  }, [visible, appServiceOnEdit]);

  const isCreateOperation = appServiceOnEdit === null;

  return (
    <Modal cancel={cancel} visible={visible} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Name</span>
          <Input
            value={formData.name}
            onChange={value => handleChange("name", value)}
            placeholder="Enter appService name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">App Code</span>
          </div>
          <Input
            type="number"
            value={formData.appCode}
            onChange={value => handleChange("appCode", value)}
            placeholder="Enter appCode"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Bootstrap Server</span>
          </div>
          <Input
            value={formData.bootstrapServer}
            onChange={value => handleChange("bootstrapServer", value)}
            placeholder="Enter bootstrapServer"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <span className="question">Group Id</span>
          <Input
            value={formData.groupId}
            onChange={value => handleChange("groupId", value)}
            placeholder="Enter groupId"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <span className="question">Consumer Queue</span>
          <Input
            value={formData.consumerQueue}
            onChange={value => handleChange("consumerQueue", value)}
            placeholder="Enter consumerQueue"
            responsive
            required
          />
        </div>

        <br />
        <div className="flex spaced end vertical-margin">
          <Button type="accent" onClick={() => cancel?.()}>
            Cancel
          </Button>
          <Button buttonType="submit" loading={isSaving}>
            {appServiceOnEdit ? "Edit App Service" : "Create App Service"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface appServiceEditModalProps {
  appService: AppServiceCreations | null;
  cancel: () => void;
  refresh: () => void;
  visible: boolean;
}

const AppServiceViewModal: FunctionComponent<appServiceEditModalProps> = ({
  appService: _appService,
  cancel,
  visible,
  refresh
}) => {
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<AppServiceCreations | null>(
    _appService
  );

  useEffect(() => {
    setServices(_appService);
  }, [_appService]);

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>Id:</span>
            <span className={styles.value}>{services?.id || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Name:</span>
            <span className={styles.value}>{services?.name || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>App Code:</span>
            <span className={styles.value}>{services?.appCode || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Bootstrap Server :</span>
            <span className={styles.value}>
              {services?.bootstrapServer || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Consumer Queue:</span>
            <span className={styles.value}>
              {services?.consumerQueue || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Group Id:</span>
            <span className={styles.value}>{services?.groupId || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Auditable Id:</span>
            <span className={styles.value}>
              {services?.auditableId || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Auditable Name:</span>
            <span className={styles.value}>
              {services?.auditableName || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Created by:</span>
            <span className={styles.value}>{services?.createdBy || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Created Date:</span>
            <span className={styles.value}>
              {services?.createdDate || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Last Modified By:</span>
            <span className={styles.value}>
              {services?.lastModifiedBy || "N/A"}
            </span>
          </div>
        </section>
      )}
    </Modal>
  );
};

export default AppService;
